/**
 * `statementImports.*` — bank-statement CSV upload + the workbench
 * mutations (approve · manually-match · categorise · defer · bulk-approve).
 *
 * Powered by migration 0024 (base tables) + 0025 (confidence + alternates +
 * categorised/deferred statuses).
 *
 * @see prototype/cash/reconcile.html
 */
import { z } from "zod";
import { isoDate, moneyPaise, uuid } from "../_common.js";

// ── shared shapes ──────────────────────────────────────────────────

const matchStatus = z.enum(["auto", "manual", "unmatched", "categorised", "deferred"]);
const matchedEntityType = z.enum(["receipt", "vendor_payment", "cash_movement"]);

const scoredCandidate = z.object({
  entityType: matchedEntityType,
  entityId: uuid,
  confidence: z.number().int().min(0).max(100),
  why: z.string(),
  label: z.string(),
  meta: z.object({
    occurredOn: isoDate,
    signedPaise: z.string().regex(/^-?\d+$/),
    docNumber: z.string().nullable().optional(),
    counterpartyLabel: z.string().nullable().optional(),
  }),
});

const statementRowProjection = z.object({
  id: uuid,
  sequenceNo: z.number().int(),
  valueDate: isoDate,
  signedPaise: z.string().regex(/^-?\d+$/),
  counterparty: z.string().nullable(),
  narration: z.string().nullable(),
  referenceNo: z.string().nullable(),
  matchStatus,
  matchedEntityType: matchedEntityType.nullable(),
  matchedEntityId: uuid.nullable(),
  confidence: z.number().int().min(0).max(100).nullable(),
  matchWhy: z.string().nullable(),
  alternates: z.array(scoredCandidate),
  createdCashMovementId: uuid.nullable(),
});

// ── inputs ──────────────────────────────────────────────────────

/** One CSV row, post-parse. signedPaise = positive for credits. */
const parsedRow = z.object({
  sequenceNo: z.number().int().nonnegative(),
  valueDate: isoDate,
  signedPaise: z.string().regex(/^-?\d+$/, "signed paise as integer string"),
  counterparty: z.string().max(200).nullable().optional(),
  narration: z.string().max(500).nullable().optional(),
  referenceNo: z.string().max(100).nullable().optional(),
});

export const uploadStatementInput = z.object({
  bankAccountId: uuid,
  filename: z.string().min(1).max(200),
  fileSha256: z.string().length(64),
  rows: z.array(parsedRow).min(1).max(2000),
});

export const listImportsInput = z.object({
  bankAccountId: uuid.optional(),
  limit: z.number().int().min(1).max(200).optional(),
});

export const listUnmatchedInput = z.object({
  bankAccountId: uuid,
  limit: z.number().int().min(1).max(500).optional(),
});

export const listMatchesInput = z.object({
  bankAccountId: uuid,
  tab: z.enum(["to_match", "auto_matched", "all"]),
  limit: z.number().int().min(1).max(500).optional(),
});

export const approveRowInput = z.object({ rowId: uuid });

export const bulkApproveInput = z.object({
  rowIds: z.array(uuid).min(1).max(200),
});

export const manuallyMatchRowInput = z.object({
  rowId: uuid,
  entityType: matchedEntityType,
  entityId: uuid,
});

export const categoriseRowInput = z.object({
  rowId: uuid,
  kind: z.enum([
    "petty_cash_expense",
    "bank_charge",
    "statutory_payment",
    "interest_paid",
    "owner_drawing",
    "owner_contribution",
    "refund_customer",
    "deposit_paid",
    "deposit_received",
  ]),
  narration: z.string().min(1).max(500),
  counterpartyLabel: z.string().max(200).optional(),
});

export const deferRowInput = z.object({ rowId: uuid });

// ── outputs ─────────────────────────────────────────────────────

export const uploadStatementOutput = z.object({
  importId: uuid,
  totalRows: z.number().int(),
  matchedRows: z.number().int(),
  unmatchedRows: z.number().int(),
  periodStart: isoDate,
  periodEnd: isoDate,
});

const importRowShape = z.object({
  id: uuid,
  bankAccountId: uuid,
  filename: z.string(),
  periodStart: isoDate,
  periodEnd: isoDate,
  totalRows: z.number().int(),
  matchedRows: z.number().int(),
  unmatchedRows: z.number().int(),
  uploadedAt: z.string(),
  uploadedByUserId: uuid,
});

export const listImportsOutput = z.object({
  rows: z.array(importRowShape),
});

export const listUnmatchedOutput = z.object({
  rows: z.array(statementRowProjection),
});

export const listMatchesOutput = z.object({
  rows: z.array(statementRowProjection),
  counts: z.object({
    toMatch: z.number().int(),
    autoMatched: z.number().int(),
    all: z.number().int(),
  }),
});

export const rowMutationOutput = z.object({
  row: statementRowProjection,
});

export const bulkApproveOutput = z.object({
  approved: z.array(statementRowProjection),
  skipped: z.array(
    z.object({
      rowId: uuid,
      reason: z.enum(["not_found", "not_unmatched", "no_candidate"]),
    }),
  ),
});

// ── derived TS types ───────────────────────────────────────────
export type UploadStatementInput = z.infer<typeof uploadStatementInput>;
export type UploadStatementOutput = z.infer<typeof uploadStatementOutput>;
export type ListImportsInput = z.infer<typeof listImportsInput>;
export type ListImportsOutput = z.infer<typeof listImportsOutput>;
export type ListUnmatchedInput = z.infer<typeof listUnmatchedInput>;
export type ListUnmatchedOutput = z.infer<typeof listUnmatchedOutput>;
export type ListMatchesInput = z.infer<typeof listMatchesInput>;
export type ListMatchesOutput = z.infer<typeof listMatchesOutput>;
export type ApproveRowInput = z.infer<typeof approveRowInput>;
export type BulkApproveInput = z.infer<typeof bulkApproveInput>;
export type BulkApproveOutput = z.infer<typeof bulkApproveOutput>;
export type ManuallyMatchRowInput = z.infer<typeof manuallyMatchRowInput>;
export type CategoriseRowInput = z.infer<typeof categoriseRowInput>;
export type DeferRowInput = z.infer<typeof deferRowInput>;
export type RowMutationOutput = z.infer<typeof rowMutationOutput>;
export type ScoredCandidate = z.infer<typeof scoredCandidate>;
export type StatementRowProjection = z.infer<typeof statementRowProjection>;

// Tree-shake bookkeeping (parsedRow + moneyPaise are referenced indirectly).
void moneyPaise;
void parsedRow;
